// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useAskPunttDesktopNotifications.test.ts
import {
  NotificationType,
  type Notification,
} from "@mg/schemas/src/commons/user";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useUiPrefs } from "./useUiPrefs";

import { canAccessAskPuntt } from "../../../utils/auth";
import { useAskPunttAllChats } from "../../../utils/queries/askPuntt";
import { useAskPunttMentionNotificationsProfile } from "../../../utils/queries/userProfile";
import {
  ASK_PUNTT_UNREAD_HIDDEN_POLL_MS,
  buildUnreadChatSnapshots,
  findNewlyUnreadChats,
  getDesktopNotificationPermission,
  isDesktopNotificationSupported,
  showAskPunttChatDesktopNotification,
  type UnreadChatSnapshot,
  resolveActiveAskPunttChatId,
} from "../utils/askPunttDesktopNotifications";

function mentionNotificationKey(notification: Notification): string {
  return [
    String(notification.createdAt),
    String(notification.reference ?? ""),
    notification.message ?? "",
    notification.ctaLink ?? "",
  ].join("\u001f");
}

function mentionNotificationTarget(ctaLink: string): {
  chatId: string;
  projectId?: string;
} | null {
  const pathname = new URL(ctaLink, window.location.origin).pathname;
  const askPunttIndex = pathname.indexOf("/ask-puntt/");
  if (askPunttIndex < 0) return null;
  const askPunttPath = pathname.slice(askPunttIndex);

  const projectChatMatch = askPunttPath.match(
    /^\/ask-puntt\/projects\/([^/]+)\/chats\/([^/]+)(?:\/|$)/,
  );
  if (projectChatMatch?.[1] && projectChatMatch[2]) {
    return {
      projectId: projectChatMatch[1],
      chatId: projectChatMatch[2],
    };
  }

  const standaloneMatch = askPunttPath.match(/^\/ask-puntt\/([^/]+)(?:\/|$)/);
  return standaloneMatch?.[1] ? { chatId: standaloneMatch[1] } : null;
}

/**
 * True when a mention toast we already raised for this chat also covers this
 * unread-poll row.
 *
 * The two pollers see the same event from different sides: the mention poll
 * reads `notifications`, the chat poll reads `lastMessageAt`. Those timestamps
 * are never equal in production — the message row is written first
 * (`lastMessageAt` comes straight off `message.createdAt`) and the mention
 * fan-out stamps its own `new Date()` a few ms later — so comparing them for
 * equality (the `lastNotifiedAtByChat` ledger the chat poller keeps) can only
 * ever dedupe against itself, never across the two pollers.
 *
 * Ordering is the reliable signal instead: any row whose newest message landed
 * at or before the mention we already toasted IS that mention arriving a second
 * time. A genuinely newer message sorts strictly after it and still notifies.
 */
function isCoveredByMentionToast(
  snapshot: UnreadChatSnapshot,
  mentionNotifiedAtByChat: ReadonlyMap<string, number>,
): boolean {
  const mentionedAt = mentionNotifiedAtByChat.get(snapshot.chatId);
  if (mentionedAt == null) return false;
  if (snapshot.lastMessageAt == null) return true;
  const lastMessageAt = Date.parse(snapshot.lastMessageAt);
  if (!Number.isFinite(lastMessageAt)) return false;
  return lastMessageAt <= mentionedAt;
}

/**
 * Polls chat lists while the tab is hidden and raises native Notification
 * toasts when a background chat becomes unread. Opt-in via ui prefs.
 */
export function useAskPunttDesktopNotifications(): void {
  const navigate = useNavigate();
  const { prefs } = useUiPrefs();
  const enabled = prefs.askPunttDesktopNotifications === true;

  const [, setVisibilityTick] = useState(0);

  const location = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      search: state.location.search as Record<string, unknown>,
    }),
  });
  const activeChatId = resolveActiveAskPunttChatId(location);

  const previousSnapshotsRef = useRef<Map<string, UnreadChatSnapshot>>(
    new Map(),
  );
  const lastNotifiedAtByChatRef = useRef<Map<string, string>>(new Map());
  const hasSeededRef = useRef(false);
  const seenMentionKeysRef = useRef<Set<string>>(new Set());
  const mentionNotifiedAtByChatRef = useRef<Map<string, number>>(new Map());
  const hasSeededMentionsRef = useRef(false);
  const mentionPollingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      setVisibilityTick((tick) => tick + 1);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const documentHidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";
  const permission = getDesktopNotificationPermission();
  const canNotify =
    enabled &&
    permission === "granted" &&
    canAccessAskPuntt() &&
    isDesktopNotificationSupported();
  const shouldPoll = canNotify && documentHidden;

  useEffect(() => {
    if (!shouldPoll) {
      hasSeededRef.current = false;
    }
  }, [shouldPoll]);

  // `refetchIntervalInBackground` is load-bearing on both polls: React Query
  // only runs an interval refetch while `focusManager.isFocused()`, which is
  // false exactly when `document.visibilityState === "hidden"` — the only state
  // in which `shouldPoll` is ever true. Without it the chat list is fetched
  // once (and that first response is swallowed by the seed pass below), so no
  // unread toast could ever fire.
  // why: each hook must declare and forward this to useQuery; this file mocks both so the real forward is pinned in utils/queries/__tests__/askPuntt.test.tsx.
  const { data } = useAskPunttAllChats({
    scope: "all",
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? ASK_PUNTT_UNREAD_HIDDEN_POLL_MS : false,
    refetchIntervalInBackground: true,
  });
  const { data: profile } = useAskPunttMentionNotificationsProfile({
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? ASK_PUNTT_UNREAD_HIDDEN_POLL_MS : false,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (shouldPoll) {
      mentionPollingStartedAtRef.current ??= Date.now();
      return;
    }
    mentionPollingStartedAtRef.current = null;
    hasSeededMentionsRef.current = false;
    seenMentionKeysRef.current.clear();
    mentionNotifiedAtByChatRef.current.clear();
  }, [shouldPoll]);

  useEffect(() => {
    if (!shouldPoll || profile?.notifications == null) return;

    const mentions = profile.notifications.filter(
      (notification) => notification.type === NotificationType.PROJECT_MENTION,
    );
    const isInitialBatch = !hasSeededMentionsRef.current;
    hasSeededMentionsRef.current = true;

    for (const notification of [...mentions].reverse()) {
      const key = mentionNotificationKey(notification);
      if (seenMentionKeysRef.current.has(key)) continue;
      seenMentionKeysRef.current.add(key);
      if (isInitialBatch) {
        const createdAt = new Date(notification.createdAt).getTime();
        const startedAt = mentionPollingStartedAtRef.current;
        if (
          startedAt == null ||
          !Number.isFinite(createdAt) ||
          createdAt < startedAt
        ) {
          continue;
        }
      }
      if (!notification.ctaLink) continue;
      const target = mentionNotificationTarget(notification.ctaLink);
      if (!target) continue;

      const snapshot: UnreadChatSnapshot = {
        chatId: target.chatId,
        projectId: target.projectId,
        title: notification.message ?? "You were mentioned in Ask Puntt",
        hasUnread: true,
        lastMessageAt: String(notification.createdAt),
      };
      showAskPunttChatDesktopNotification(snapshot, (selected) => {
        if (selected.projectId) {
          void navigate({
            to: "/ask-puntt/projects/$projectId",
            params: { projectId: selected.projectId },
            search: { tab: "chats", chatId: selected.chatId },
          });
          return;
        }
        void navigate({
          to: "/ask-puntt/$chatId",
          params: { chatId: selected.chatId },
        });
      });
      const notifiedAt = new Date(notification.createdAt).getTime();
      if (Number.isFinite(notifiedAt)) {
        mentionNotifiedAtByChatRef.current.set(snapshot.chatId, notifiedAt);
      }
    }
  }, [navigate, profile?.notifications, shouldPoll]);

  useEffect(() => {
    if (!shouldPoll || data?.chats == null) return;

    const nextSnapshots = buildUnreadChatSnapshots(data.chats);

    if (!hasSeededRef.current) {
      for (const snapshot of Array.from(nextSnapshots.values())) {
        if (snapshot.hasUnread && snapshot.lastMessageAt != null) {
          lastNotifiedAtByChatRef.current.set(
            snapshot.chatId,
            snapshot.lastMessageAt,
          );
        }
      }
      previousSnapshotsRef.current = nextSnapshots;
      hasSeededRef.current = true;
      return;
    }

    const newlyUnread = findNewlyUnreadChats(
      previousSnapshotsRef.current,
      nextSnapshots,
      {
        activeChatId,
        lastNotifiedAtByChat: lastNotifiedAtByChatRef.current,
      },
    );

    for (const snapshot of newlyUnread) {
      if (
        isCoveredByMentionToast(snapshot, mentionNotifiedAtByChatRef.current)
      ) {
        // Same event, second poller. Keep the mention toast we already raised:
        // it carries "<actor> mentioned you in <chat>", and both toasts share
        // the `ask-puntt-chat-<id>` tag, so letting this one through would
        // replace that text with the generic "New message in Ask Puntt".
        continue;
      }
      showAskPunttChatDesktopNotification(snapshot, (target) => {
        if (target.projectId) {
          void navigate({
            to: "/ask-puntt/projects/$projectId",
            params: { projectId: target.projectId },
            search: { tab: "chats", chatId: target.chatId },
          });
          return;
        }
        void navigate({
          to: "/ask-puntt/$chatId",
          params: { chatId: target.chatId },
        });
      });
      if (snapshot.lastMessageAt != null) {
        lastNotifiedAtByChatRef.current.set(
          snapshot.chatId,
          snapshot.lastMessageAt,
        );
      }
    }

    previousSnapshotsRef.current = nextSnapshots;
  }, [activeChatId, data, navigate, shouldPoll]);
}
